import { useState, useEffect, useRef } from "react";
import Logo from "@/components/Logo";

const STEPS = [
  "Google Business Profile",
  "Local Pack Rankings",
  "Geo-Grid Analysis",
  "Citation Health",
  "Website SEO Audit",
  "Website Speed Check",
  "Reviews & Reputation",
  "AI Search Visibility",
  "Competitor Analysis",
  "Social Presence",
  "Backlink Analysis",
  "Revenue Calculation",
];

export default function LoadingScreen({ businessName, placeId, onComplete, auditData }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [animationDone, setAnimationDone] = useState(false);
  const [waitingSeconds, setWaitingSeconds] = useState(0);
  const auditDataRef = useRef(null);

  // Store audit data when it arrives
  useEffect(() => {
    if (auditData) auditDataRef.current = auditData;
  }, [auditData]);

  // Animation timer: ~1.8s per step for faster perceived speed
  useEffect(() => {
    let step = 0;
    const timer = setInterval(() => {
      step++;
      setCurrentStep(step);
      if (step >= STEPS.length) {
        clearInterval(timer);
        setAnimationDone(true);
      }
    }, 1800 + Math.random() * 300);
    return () => clearInterval(timer);
  }, []);

  // After animation done, count seconds while waiting for data
  useEffect(() => {
    if (!animationDone) return;
    const timer = setInterval(() => {
      setWaitingSeconds(s => s + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [animationDone]);

  // Navigate only when BOTH: data received AND animation complete
  useEffect(() => {
    if (animationDone && auditDataRef.current) {
      const timer = setTimeout(() => onComplete(auditDataRef.current), 400);
      return () => clearTimeout(timer);
    }
    if (animationDone && !auditDataRef.current) {
      const check = setInterval(() => {
        if (auditDataRef.current) {
          clearInterval(check);
          onComplete(auditDataRef.current);
        }
      }, 500);
      return () => clearInterval(check);
    }
  }, [animationDone, onComplete]);

  const progressPct = Math.round((currentStep / STEPS.length) * 100);
  const isWaitingForData = animationDone && !auditDataRef.current;

  // Dynamic messages while waiting after 100%
  const getWaitMessage = () => {
    if (waitingSeconds < 10) return "Compiling your results...";
    if (waitingSeconds < 25) return "Running deep competitor analysis...";
    if (waitingSeconds < 45) return "Calculating your revenue impact — almost there...";
    if (waitingSeconds < 70) return "This business has a lot of data — hang tight, it's worth the wait...";
    return "Final checks — your report is nearly ready...";
  };

  return (
    <div style={{
      minHeight: "100vh", background: "#fff",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: 24,
    }} data-testid="loading-screen">
      <Logo size="large" theme="light" />

      <h2 style={{
        fontSize: 22, fontWeight: 800, color: "#111827",
        marginTop: 32, marginBottom: 4, textAlign: "center",
      }}>
        Analyzing {businessName || "your business"}...
      </h2>
      <p style={{ fontSize: 14, color: "#6B7280", marginBottom: 32, textAlign: "center" }}>
        Running a comprehensive local SEO audit
      </p>

      {/* Progress bar */}
      <div style={{ width: "100%", maxWidth: 420, marginBottom: 32 }}>
        <div style={{
          height: 8, borderRadius: 4, background: "#E5E7EB", overflow: "hidden",
        }}>
          <div style={{
            height: "100%", borderRadius: 4,
            background: isWaitingForData
              ? "linear-gradient(90deg, #6366f1, #8b5cf6, #6366f1)"
              : "linear-gradient(90deg, #6366f1, #8b5cf6)",
            width: `${progressPct}%`,
            transition: "width 0.8s ease",
            backgroundSize: isWaitingForData ? "200% 100%" : "100% 100%",
            animation: isWaitingForData ? "shimmer 1.5s ease-in-out infinite" : "none",
          }} />
        </div>
        <p style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: "#6366f1", marginTop: 6 }}>
          {progressPct}%
        </p>
      </div>

      {/* Step checklist */}
      <div style={{ width: "100%", maxWidth: 340, marginBottom: 24 }}>
        {STEPS.map((step, i) => {
          const isDone = currentStep > i;
          const isActive = currentStep === i;
          const isPending = currentStep < i;

          return (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "8px 0",
              opacity: isPending ? 0.4 : 1,
              transition: "opacity 0.4s ease",
            }} data-testid={`loading-step-${i}`}>
              <span style={{ width: 24, display: "flex", justifyContent: "center" }}>
                {isDone ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : isActive ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5"
                    style={{ animation: "spin 1s linear infinite" }}>
                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                )}
              </span>
              <span style={{
                fontSize: 14, fontWeight: isDone || isActive ? 600 : 400,
                color: isDone ? "#22c55e" : isActive ? "#6366f1" : "#9CA3AF",
                transition: "color 0.3s ease",
              }}>
                {step}{isActive ? "..." : ""}
              </span>
            </div>
          );
        })}
      </div>

      {/* Dynamic status message */}
      {isWaitingForData ? (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          padding: "16px 24px", borderRadius: 12,
          background: "#f5f3ff", border: "1px solid #e0e7ff",
          maxWidth: 380,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5"
              style={{ animation: "spin 1.2s linear infinite", flexShrink: 0 }}>
              <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
            </svg>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#4338ca" }}>
              {getWaitMessage()}
            </span>
          </div>
          <p style={{ fontSize: 12, color: "#6366f1", textAlign: "center" }}>
            We're collecting real-time data from multiple sources. Please don't close this page.
          </p>
        </div>
      ) : (
        <p style={{ fontSize: 13, color: "#9CA3AF", textAlign: "center", maxWidth: 320, lineHeight: 1.6 }}>
          This usually takes 15-30 seconds for a complete analysis...
        </p>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>
    </div>
  );
}
